import React from 'react';
import {Document, DocumentData} from "types/Document";
import {Form, Formik, FormikHelpers} from "formik";
import Field from "components/Form/Field";
import FieldHeader from "components/Form/FieldHeader";
import Uploader from "components/Form/Uploader";
import FormControls from "components/FormControls";
import {DocumentIcon} from "@heroicons/react/outline";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {TextAreaField} from "../../../components/Form/Form";

type Props = {
    document?: Document | null
    onSubmit: (values: DocumentData, helpers: FormikHelpers<DocumentData>) => any
}

function DocumentForm(props: Props) {
    const validationSchema = Yup.object().shape({
        name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
        description: Yup.string().max(255, 'Maximum character limit exceeded'),
        asset: Yup.object().shape({
            id: Yup.number().moreThan(0, "Please upload an asset")
        })
    })

    const initialValues: DocumentData = {
        name: props.document?.name || '',
        description: props.document?.description || '',
        asset: {id: props.document?.asset.id || 0},
    }
    
    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
            {formik => {
                return (
                    <Form className="space-y-8 divide-y divide-gray-200">
                        <ErrorNotification formik={formik} />
                        
                        <div className="shadow sm:rounded-md">
                            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                                <div className="space-y-8 divide-y divide-gray-200">
                                    <div>
                                        <div>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                Key Information
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                The document's name, description and asset
                                            </p>
                                        </div>
                                        <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                                            {/* Name */}
                                            <div className="sm:col-span-4 xl:col-span-3">
                                                <Field
                                                    label="Name *"
                                                    hint='255 chars max'
                                                    type="text"
                                                    name="name"
                                                    maxLength={255}
                                                    autoFocus
                                                />
                                            </div>

                                            {/* Description */}
                                            <div className="sm:col-span-6">
                                                <TextAreaField
                                                    label="Description"
                                                    hint="Optional. 255 chars max."
                                                    rows={6}
                                                    name="description"
                                                    maxChars={255}
                                                />
                                            </div>

                                            {/* Asset */}
                                            <div className="sm:col-span-6">
                                                <FieldHeader label="Asset *" />
                                                <Uploader
                                                    icon={DocumentIcon}
                                                    files={props.document?.asset}
                                                    type={Uploader.types.DOCUMENT}
                                                    maxFiles={1}
                                                    onChange={files => formik.setFieldValue('asset', {id: files[0]?.id || 0})}
                                                />

                                                {/* @ts-ignore */}
                                                {(!!formik.errors?.asset?.id && formik.touched?.asset?.id) &&
                                                  <FieldHelp isError>
                                                      {/* @ts-ignore */}
                                                      {formik.errors.asset?.id}
                                                  </FieldHelp>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <FormControls formik={formik} />
                        </div>
                    </Form>
                )
            }}
        </Formik>
    );
}

export default DocumentForm;